import { useEffect, useCallback, useRef, useState } from 'react';
import { Platform, View, ActivityIndicator, StyleSheet, Animated, Image, AppState } from 'react-native';
import { Stack } from 'expo-router';
import { StatusBar } from 'expo-status-bar';
import { useFonts } from 'expo-font';
import * as SplashScreen from 'expo-splash-screen';
import {
  Oswald_400Regular,
  Oswald_500Medium,
  Oswald_600SemiBold,
  Oswald_700Bold,
} from '@expo-google-fonts/oswald';
import {
  Inter_400Regular,
  Inter_500Medium,
  Inter_600SemiBold,
  Inter_700Bold,
} from '@expo-google-fonts/inter';
import {
  BarlowCondensed_400Regular,
  BarlowCondensed_600SemiBold,
  BarlowCondensed_700Bold,
  BarlowCondensed_800ExtraBold,
} from '@expo-google-fonts/barlow-condensed';
import * as Notifications from 'expo-notifications';
import { useFrameworkReady } from '@/hooks/useFrameworkReady';
import { AuthProvider } from '@/contexts/AuthContext';
import { ThemeProvider } from '@/contexts/ThemeContext';
import { router } from 'expo-router';
import { GestureHandlerRootView } from 'react-native-gesture-handler';
import ErrorBoundary from '@/components/ErrorBoundary';
import NetworkStatusBanner from '@/components/NetworkStatusBanner';
import OfflineBar from '@/components/OfflineBar';
import OfflineIndicator from '@/components/OfflineIndicator';
import '@/lib/linking';
import { installUrlGuard } from '@/lib/urlGuard';
import { initErrorReporting } from '@/lib/errorReporting';
import { initAnalytics } from '@/lib/analytics';
import { initRevenueCat } from '@/lib/revenueCat';
import { checkIntegrity } from '@/lib/tamperDetection';

SplashScreen.preventAutoHideAsync();
installUrlGuard();

// Patch: guard against Expo Router reading .hash off an undefined URL object.
// This causes 200+ "Cannot read property 'hash' of undefined" Sentry events on cold start.
// The underlying cause is a race condition in expo-router's history restoration on native.
if (typeof global !== 'undefined') {
  const _originalGlobalHandler = (global as any).ErrorUtils?.getGlobalHandler?.();
  (global as any).ErrorUtils?.setGlobalHandler?.((error: Error, isFatal: boolean) => {
    if (error?.message?.includes("property 'hash' of undefined") ||
        error?.message?.includes("Cannot read properties of undefined (reading 'hash')")) {
      // Non-fatal expo-router URL race condition — suppress, do not crash
      return;
    }
    if (_originalGlobalHandler) _originalGlobalHandler(error, isFatal);
  });
}

initErrorReporting();
initAnalytics();
// Initialize RevenueCat for native IAP (no-op on web)
if (typeof process !== 'undefined') {
  initRevenueCat().catch(() => {});
}

// Tamper detection — report silently, don't block
try {
  const integrity = checkIntegrity();
  if (!integrity.isSecure) {
    // Report will happen after auth when userId is available
    // Store flags for later reporting
    (globalThis as any).__duelIntegrityFlags = integrity.flags;
  }
} catch {}

/**
 * Load Google Fonts on web and alias expo-google-fonts names to real font URLs.
 * 
 * Expo's font system on web uses names like "Oswald_700Bold" as the CSS font-family.
 * We fetch the Google Fonts CSS to get the real woff2 URLs, then register @font-face
 * rules mapping each expo name → the correct woff2 URL.
 */
function useWebFonts() {
  useEffect(() => {
    if (Platform.OS !== 'web') return;
    if (document.getElementById('duel-font-map')) return;

    const FONT_MAP: Record<string, [string, string]> = {
      // [expoName]: [googleFamily, weight]
      'Oswald_400Regular': ['Oswald', '400'],
      'Oswald_500Medium': ['Oswald', '500'],
      'Oswald_600SemiBold': ['Oswald', '600'],
      'Oswald_700Bold': ['Oswald', '700'],
      'Inter_400Regular': ['Inter', '400'],
      'Inter_500Medium': ['Inter', '500'],
      'Inter_600SemiBold': ['Inter', '600'],
      'Inter_700Bold': ['Inter', '700'],
      'BarlowCondensed_400Regular': ['Barlow Condensed', '400'],
      'BarlowCondensed_600SemiBold': ['Barlow Condensed', '600'],
      'BarlowCondensed_700Bold': ['Barlow Condensed', '700'],
      'BarlowCondensed_800ExtraBold': ['Barlow Condensed', '800'],
    };

    const cssUrl = 'https://fonts.googleapis.com/css2?family=Oswald:wght@400;500;600;700&family=Inter:wght@400;500;600;700&family=Barlow+Condensed:wght@400;600;700;800&display=swap';

    fetch(cssUrl)
      .then((res) => res.text())
      .then((cssText) => {
        // Parse @font-face blocks from Google's CSS
        // Each block has font-family, font-weight, and src with url()
        const blocks = cssText.match(/@font-face\s*\{[^}]+\}/g) || [];
        const urlMap: Record<string, string> = {}; // "Family|weight" → url

        for (const block of blocks) {
          const familyMatch = block.match(/font-family:\s*'([^']+)'/);
          const weightMatch = block.match(/font-weight:\s*(\d+)/);
          const urlMatch = block.match(/url\(([^)]+)\)\s*format\('woff2'\)/);
          if (familyMatch && weightMatch && urlMatch) {
            urlMap[`${familyMatch[1]}|${weightMatch[1]}`] = urlMatch[1];
          }
        }

        let css = '';
        for (const [expoName, [family, weight]] of Object.entries(FONT_MAP)) {
          const url = urlMap[`${family}|${weight}`];
          if (url) {
            css += `@font-face { font-family: '${expoName}'; src: url(${url}) format('woff2'); font-weight: ${weight}; font-style: normal; font-display: swap; }\n`;
          }
        }

        if (css) {
          const style = document.createElement('style');
          style.id = 'duel-font-map';
          style.textContent = css;
          document.head.appendChild(style);
        }
      })
      .catch(() => {
        // Font loading failed silently — app still works with system fonts
      });
  }, []);
}

/** Hide/minimize scrollbars on web for a cleaner look */
function useHideWebScrollbars() {
  useEffect(() => {
    if (Platform.OS !== 'web') return;
    const id = 'duel-scrollbar-style';
    if (document.getElementById(id)) return;
    const style = document.createElement('style');
    style.id = id;
    style.textContent = `
      * { scrollbar-width: thin; scrollbar-color: rgba(255,255,255,0.15) transparent; }
      *::-webkit-scrollbar { width: 4px; height: 4px; }
      *::-webkit-scrollbar-track { background: transparent; }
      *::-webkit-scrollbar-thumb { background: rgba(255,255,255,0.15); border-radius: 4px; }
      *::-webkit-scrollbar-thumb:hover { background: rgba(255,255,255,0.25); }
    `;
    document.head.appendChild(style);
  }, []);
}

export default function RootLayout() {
  useFrameworkReady();
  useWebFonts();
  useHideWebScrollbars();

  // Handle push notification taps (deep linking)
  useEffect(() => {
    const subscription = Notifications.addNotificationResponseReceivedListener(response => {
      const data = response.notification.request.content.data;

      if (data?.type === 'duel_challenge' || data?.type === 'duel_accepted' || data?.type === 'duel_complete') {
        router.push(`/duels/${data.duelId}` as any);
      } else if (data?.type === 'duel_lift') {
        router.push(`/duels/${data.duelId}` as any);
      } else if (data?.type === 'new_follower') {
        router.push('/(tabs)/friends' as any);
      } else if (data?.type === 'challenge_reminder') {
        router.push('/(tabs)/home' as any);
      }
    });

    return () => subscription.remove();
  }, []);

  // Handle foreground notifications – update badge count
  useEffect(() => {
    const subscription = Notifications.addNotificationReceivedListener(() => {
      Notifications.getBadgeCountAsync().then(count => {
        Notifications.setBadgeCountAsync(count + 1);
      });
    });

    return () => subscription.remove();
  }, []);

  // Clear badge count when app comes to foreground
  useEffect(() => {
    const subscription = AppState.addEventListener('change', (state) => {
      if (state === 'active') {
        Notifications.setBadgeCountAsync(0);
      }
    });
    // Also clear on mount
    Notifications.setBadgeCountAsync(0);
    return () => subscription.remove();
  }, []);

  const [fontsLoaded, fontError] = useFonts({
    Oswald_400Regular,
    Oswald_500Medium,
    Oswald_600SemiBold,
    Oswald_700Bold,
    Inter_400Regular,
    Inter_500Medium,
    Inter_600SemiBold,
    Inter_700Bold,
    BarlowCondensed_400Regular,
    BarlowCondensed_600SemiBold,
    BarlowCondensed_700Bold,
    BarlowCondensed_800ExtraBold,
  });

  // Safety timeout — force hide splash after 4 seconds no matter what
  const [fontTimeout, setFontTimeout] = useState(false);
  useEffect(() => {
    const t = setTimeout(() => {
      setFontTimeout(true);
      SplashScreen.hideAsync().catch(() => {});
    }, 4000);
    return () => clearTimeout(t);
  }, []);

  const appReady = fontsLoaded || fontError || fontTimeout;

  const onLayoutRootView = useCallback(async () => {
    if (appReady) {
      SplashScreen.hideAsync().catch(() => {});
    }
  }, [appReady]);

  if (!appReady) {
    return <SplashLogo />;
  }

  return (
    <GestureHandlerRootView style={{ flex: 1 }} onLayout={onLayoutRootView}>
      <ErrorBoundary>
      <NetworkStatusBanner />
      {/* OfflineBar and OfflineIndicator removed -- too disruptive, offline queue still works silently */}
      <AuthProvider>
        <ThemeProvider>
          <Stack screenOptions={{ headerShown: false }}>
            <Stack.Screen name="index" options={{ animation: 'none' }} />
            <Stack.Screen name="(auth)" />
            <Stack.Screen name="(onboarding)" />
            <Stack.Screen name="(tabs)" options={{ animation: 'none' }} />
            <Stack.Screen name="gate" options={{ animation: 'none' }} />
            <Stack.Screen name="reset-password" />
            <Stack.Screen name="debug-auth" />
            <Stack.Screen name="u/[handle]" options={{ headerShown: false }} />
            <Stack.Screen name="coach-dashboard" options={{ headerShown: false }} />
            <Stack.Screen name="coach-client-detail" options={{ headerShown: false }} />
            <Stack.Screen name="coach-client-roster" options={{ headerShown: false }} />
            <Stack.Screen name="coach-schedule" options={{ headerShown: false }} />
            <Stack.Screen name="org-dashboard" options={{ headerShown: false }} />
            <Stack.Screen name="owner-dashboard" options={{ headerShown: false }} />
            <Stack.Screen name="teams" options={{ headerShown: false }} />
            <Stack.Screen name="club" options={{ headerShown: false }} />
            <Stack.Screen name="club-home" options={{ headerShown: false }} />
            <Stack.Screen name="club-setup" options={{ headerShown: false }} />
            <Stack.Screen name="club-onboarding" options={{ headerShown: false }} />
            <Stack.Screen name="org-roster" options={{ headerShown: false }} />
            <Stack.Screen name="org-leaderboard" options={{ headerShown: false }} />
            <Stack.Screen name="org-comparison" options={{ headerShown: false }} />
            <Stack.Screen name="gym" options={{ headerShown: false }} />
            <Stack.Screen name="my-program" options={{ headerShown: false }} />
            <Stack.Screen name="season-standings" options={{ headerShown: false }} />
            <Stack.Screen name="challenge-leaderboard" options={{ headerShown: false }} />
            <Stack.Screen name="elo-leaderboard" options={{ headerShown: false }} />
            <Stack.Screen name="tournaments" options={{ headerShown: false }} />
            <Stack.Screen name="tournament" options={{ headerShown: false }} />
            <Stack.Screen name="rulebook" options={{ headerShown: false }} />
            <Stack.Screen name="judge-queue" options={{ headerShown: false }} />
            <Stack.Screen name="settings" options={{ headerShown: false }} />
            <Stack.Screen name="+not-found" />
          </Stack>
          <StatusBar style="light" />
        </ThemeProvider>
      </AuthProvider>
      </ErrorBoundary>
    </GestureHandlerRootView>
  );
}

function SplashLogo() {
  const opacity = useRef(new Animated.Value(0)).current;
  useEffect(() => {
    const anim = Animated.sequence([
      Animated.timing(opacity, { toValue: 1, duration: 400, useNativeDriver: false }),
      Animated.delay(600),
      Animated.timing(opacity, { toValue: 0.7, duration: 300, useNativeDriver: false }),
      Animated.timing(opacity, { toValue: 1, duration: 300, useNativeDriver: false }),
    ]);
    anim.start();
    return () => anim.stop();
  }, []);
  return (
    <View style={loadingStyles.container}>
      <Animated.Image
        source={require('@/assets/images/duel-logo.png')}
        style={[loadingStyles.logo, { opacity }]}
        resizeMode="contain"
      />
    </View>
  );
}

const loadingStyles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#030303',
    alignItems: 'center',
    justifyContent: 'center',
  },
  logo: {
    width: 160,
    height: 160,
  },
});
